import type { ViewState } from "./routes.ts"
import type { UserView } from "./user-view.ts"

type StatePanelState = Exclude<ViewState, "DEFAULT">
type StatePanelIcon = "history" | "notice" | "status"

export type StatePanelAction =
  | { readonly href: "/balance/history" | "/support/inquiries"; readonly icon: "history" | "notice"; readonly kind: "link"; readonly label: string }
  | { readonly icon: "status"; readonly kind: "reload"; readonly label: string }

type StatePanelPresentation = {
  readonly actions: readonly StatePanelAction[]
  readonly heading: string
  readonly icon: StatePanelIcon
  readonly showsRecoveryArtwork: boolean
  readonly showsSkeleton: boolean
}

const reviewActions = [
  { href: "/balance/history", icon: "history", kind: "link", label: "상태 이력 보기" },
  { href: "/support/inquiries", icon: "notice", kind: "link", label: "고객지원 문의" },
] as const satisfies readonly StatePanelAction[]

export const statePanelPresentations = {
  DISABLED: { actions: [], heading: "지금은 이 기능을 사용할 수 없습니다", icon: "notice", showsRecoveryArtwork: false, showsSkeleton: false },
  EMPTY: { actions: [], heading: "아직 표시할 내용이 없습니다", icon: "status", showsRecoveryArtwork: false, showsSkeleton: false },
  ERROR: { actions: [{ icon: "status", kind: "reload", label: "다시 불러오기" }], heading: "정보를 불러오지 못했어요", icon: "notice", showsRecoveryArtwork: true, showsSkeleton: false },
  HOLD: { actions: reviewActions, heading: "처리가 보류되어 있습니다", icon: "notice", showsRecoveryArtwork: false, showsSkeleton: false },
  LOADING: { actions: [], heading: "정보를 불러오고 있어요", icon: "status", showsRecoveryArtwork: false, showsSkeleton: true },
  MANUAL_REVIEW: { actions: reviewActions, heading: "담당자가 내역을 확인하고 있습니다", icon: "notice", showsRecoveryArtwork: false, showsSkeleton: false },
  STATUS_UNKNOWN: { actions: reviewActions, heading: "처리 결과를 확인하고 있어요", icon: "status", showsRecoveryArtwork: false, showsSkeleton: false },
} as const satisfies Readonly<Record<StatePanelState, StatePanelPresentation>>

const copy: Readonly<Record<string, string>> = {
  "conversion.completed": "포인트 전환이 완료되었습니다",
  "conversion.debit_pending": "전환 요청을 처리하고 있습니다",
  "conversion.expired": "전환 견적의 유효기간이 지났습니다",
  "conversion.manual_review": "전환 내역을 확인하고 있습니다",
  "conversion.quoted": "전환 내용을 확인해 주세요",
  "conversion.reversal_pending": "전환 금액을 복원하고 있습니다",
  "conversion.reversed": "전환 금액이 복원되었습니다",
  "conversion.risk_hold": "안전한 처리를 위해 전환을 확인하고 있습니다",
  "conversion.status_unknown": "외부 기관 응답을 확인하고 있습니다",
  "redemption.completed": "상품 교환이 완료되었습니다",
  "redemption.created": "상품 교환 내용을 확인해 주세요",
  "redemption.delivery_failed": "MMS 발송 상태를 확인할 수 없습니다",
  "redemption.issuance_pending": "상품권 발급을 처리하고 있습니다",
  "redemption.manual_review": "상품 교환 내역을 확인하고 있습니다",
  "redemption.rejected": "사용 가능한 잔액이 부족합니다",
  "redemption.balance_restored": "차감된 잔액이 복원되었습니다",
  "redemption.risk_hold": "안전한 처리를 위해 상품 교환을 확인하고 있습니다",
  "redemption.status_unknown": "외부 기관 응답을 확인하고 있습니다",
}

const labels: Readonly<Record<string, string>> = {
  BALANCE_RESTORED: "잔액 복원 완료",
  BALANCE_CAPTURED: "사용 완료",
  COMPLETED: "완료",
  CREATED: "요청됨",
  DEBIT_PENDING: "처리 중",
  DELIVERY_FAILED: "발송 확인 필요",
  EXPIRED: "만료됨",
  ISSUANCE_PENDING: "발급 처리 중",
  ISSUED: "사용 가능",
  MANUAL_REVIEW: "담당자 검토 중",
  QUOTED: "확인 필요",
  REJECTED: "처리 불가",
  REVERSAL_PENDING: "복원 처리 중",
  REVERSED: "복원 완료",
  RISK_HOLD: "안전 확인 중",
  STATUS_UNKNOWN: "응답 확인 중",
  VOID: "사용 중지",
}

export const stateCopy = (view: Extract<UserView, { readonly kind: "transaction" }>): string => copy[view.copyKey] ?? "처리 상태를 확인하고 있습니다"

export const stateLabel = (state: string): string => labels[state] ?? "상태 확인 중"
